/**
 * Offline Architectural Capture Queue (v1.2.69)
 * ==============================================
 * Parallel to services/offlineStorage.js (asset photos) and
 * services/offlineObservationQueue.js (assessor-authored observations).
 *
 * ⚠️  ISOLATION CONTRACT — DO NOT CHANGE
 * - Uses its OWN IndexedDB database `fmiq_architectural_queue_db`
 *   so the existing asset queue (`fmiq_offline_db`) can never be
 *   migrated, corrupted, or version-collided by anything here.
 * - Uses its OWN object store `pending_architectural_captures`.
 * - Exports parallel-named functions (enqueueArchitecturalCapture,
 *   drainArchitecturalQueue) — never modifies exports of the asset
 *   or observation queues.
 *
 * Storage shape (one queued item = one full Architectural Capture batch):
 *   {
 *     id: <autoinc>,
 *     assessment_id: string,
 *     images_base64: string[],  (up to 10, matches server /site-review/analyze cap)
 *     context: string | null,
 *     location: string | null,
 *     assessor_id: string,
 *     timestamp: ISO,
 *     status: 'pending' | 'uploading' | 'completed' | 'failed',
 *     retry_count: number,
 *     last_error: string | null
 *   }
 */

const DB_NAME = 'fmiq_architectural_queue_db';
const DB_VERSION = 1;
const CAPTURES_STORE = 'pending_architectural_captures';

let _db = null;
let _dbFailed = false;

const _openDB = () =>
  new Promise((resolve, reject) => {
    if (_dbFailed) return reject(new Error('Architectural queue DB previously failed to initialize'));
    if (_db) return resolve(_db);
    if (!window.indexedDB) {
      _dbFailed = true;
      return reject(new Error('IndexedDB not available'));
    }
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      const timeout = setTimeout(() => {
        _dbFailed = true;
        reject(new Error('Architectural queue DB open timed out'));
      }, 5000);
      req.onerror = () => {
        clearTimeout(timeout);
        _dbFailed = true;
        reject(req.error);
      };
      req.onblocked = () => {
        clearTimeout(timeout);
        _dbFailed = true;
        reject(new Error('Architectural queue DB blocked by another connection'));
      };
      req.onsuccess = () => {
        clearTimeout(timeout);
        _db = req.result;
        _db.onclose = () => { _db = null; };
        _db.onversionchange = () => { _db.close(); _db = null; };
        resolve(_db);
      };
      req.onupgradeneeded = (evt) => {
        const db = evt.target.result;
        if (!db.objectStoreNames.contains(CAPTURES_STORE)) {
          const s = db.createObjectStore(CAPTURES_STORE, { keyPath: 'id', autoIncrement: true });
          s.createIndex('assessment_id', 'assessment_id', { unique: false });
          s.createIndex('status', 'status', { unique: false });
          s.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    } catch (err) {
      _dbFailed = true;
      reject(err);
    }
  });

export const enqueueArchitecturalCapture = async ({
  assessment_id,
  images_base64,
  context = null,
  location = null,
  assessor_id,
  // v1.3.37.146 — vendor rows are drained by the vendor queue, not here.
  owner = null,
  floor_level = null,
  room_number = null,
}) => {
  // v1.2.101 — Empty-payload guard. Product owner symptom on iPad
  // Aramark prod: banner showed "1 item · 0 photos" and the drain
  // worker did nothing on reconnect — the row had been persisted
  // to IndexedDB with an empty images_base64 array. Root cause was
  // iOS Safari's silent canvas.toDataURL failure upstream in
  // SiteReviewDialog. That's now fixed at the source (see the
  // helper swap in SiteReviewDialog.js:75), but we ALSO refuse to
  // persist a ghost row here as defense-in-depth — the queue
  // should never carry a capture that can never drain.
  //
  // Floor: 500 chars per entry. A real 1920px JPEG at 0.85 quality
  // encodes to ~60-200 KB base64. 500 chars is well below the
  // smallest legitimate photo and clearly separates real captures
  // from "data:," (5 chars) or truncated payloads.
  if (!Array.isArray(images_base64) || images_base64.length === 0) {
    const err = new Error('Cannot queue architectural capture: no photos provided.');
    err.code = 'EMPTY_PAYLOAD';
    throw err;
  }
  const validEntries = images_base64.filter(
    (b) => typeof b === 'string' && b.length > 500,
  );
  if (validEntries.length === 0) {
    const err = new Error(
      'Cannot queue architectural capture: photos are empty or corrupt. Please re-capture and try again.',
    );
    err.code = 'CORRUPT_PAYLOAD';
    err.received_count = images_base64.length;
    throw err;
  }

  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([CAPTURES_STORE], 'readwrite');
    const store = tx.objectStore(CAPTURES_STORE);
    const record = {
      assessment_id,
      images_base64: validEntries,
      context,
      location,
      assessor_id,
      owner,
      floor_level,
      room_number,
      timestamp: new Date().toISOString(),
      status: 'pending',
      retry_count: 0,
      last_error: null,
    };
    const req = store.add(record);
    req.onsuccess = () => resolve({ id: req.result, photo_count: validEntries.length });
    req.onerror = () => reject(req.error);
  });
};

export const getPendingArchitecturalCaptures = async (assessmentId = null) => {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([CAPTURES_STORE], 'readonly');
    const store = tx.objectStore(CAPTURES_STORE);
    const req = assessmentId
      ? store.index('assessment_id').getAll(assessmentId)
      : store.getAll();
    req.onsuccess = () => {
      // v1.2.101 — Include 'uploading' rows in the pending set.
      //
      // Background: /site-review/analyze can run up to 180 s while
      // Gemini processes the batch. On iOS Safari, backgrounded tabs
      // are silently killed during long fetches — if the tab dies
      // AFTER the drain worker sets status='uploading' but BEFORE
      // the fetch resolves, the row is stranded with that status
      // forever. Prior filter (pending | failed) hid these rows
      // from both the drain worker's next pass AND from the
      // OfflineStatusBanner arch counter, producing the twin symptoms:
      //   1. Reconnect never drains previously-attempted arch batches
      //      (main problem on iPad Aramark prod).
      //   2. Banner detail "N items · M photos (X assets · Y arch)"
      //      loses the arch bucket, so the strip appears thinner than
      //      the day before even though captures exist.
      //
      // Safe to include 'uploading' because drainArchitecturalQueue
      // guards with `if (_draining) return` — concurrent drains
      // cannot race on the same row. See offlineArchitecturalSync
      // §drainArchitecturalQueue.
      const rows = (req.result || []).filter(
        (r) => r.status === 'pending'
            || r.status === 'failed'
            || r.status === 'uploading'
      );
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  });
};

export const updateCaptureStatus = async (id, status, extra = {}) => {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([CAPTURES_STORE], 'readwrite');
    const store = tx.objectStore(CAPTURES_STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const row = getReq.result;
      if (!row) return resolve(false);
      row.status = status;
      Object.assign(row, extra);
      const putReq = store.put(row);
      putReq.onsuccess = () => resolve(true);
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

export const deleteCapture = async (id) => {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([CAPTURES_STORE], 'readwrite');
    const req = tx.objectStore(CAPTURES_STORE).delete(id);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
};

export const getArchitecturalQueueStats = async () => {
  const pending = await getPendingArchitecturalCaptures();
  return {
    totalCaptures: pending.length,
    totalPhotos: pending.reduce((s, r) => s + (r.images_base64?.length || 0), 0),
  };
};
